<template>
  <b-modal
    :visible="studentDeraOpen"
    title="Distance Education Readiness Assessment"
    size="lg"
    ok-only
    ok-title="Close"
    body-class="pt-1"
    @change="onVisibilityChange"
  >
    <!-- ── Result ─────────────────────────────────────────────────────────
         The outcome is the one thing staff open this for, so it leads and it
         is the only element with real visual weight. Everything else is
         supporting detail. -->
    <div v-if="hasResult" class="d-flex align-items-center mb-1">
      <div class="mr-2 text-center" style="min-width: 5rem">
        <h1 class="mb-0 font-weight-bolder" :class="`text-${statusVariant}`">{{ deraData.latest.percentage }}%</h1>
      </div>
      <div class="flex-grow-1">
        <h4 class="mb-25 font-weight-bolder" :class="`text-${statusVariant}`">{{ statusLabel }}</h4>
        <div class="text-muted">
          {{ attemptSummary }}
          <span class="mx-25">&middot;</span>
          <span class="text-monospace">{{ deraData.latest.assessmentCode }}</span>
        </div>
        <div class="text-muted">Completed {{ formatDate(deraData.latest.completedAt) }}</div>
      </div>
    </div>

    <b-progress v-if="hasResult" :max="100" height="6px" class="mb-2">
      <b-progress-bar :value="deraData.latest.percentage" :variant="statusVariant" />
    </b-progress>

    <!-- No result yet: say what state this student is actually in, rather than
         showing an empty panel. -->
    <div v-else class="d-flex align-items-center mb-2">
      <b-badge pill :variant="statusVariant" class="mr-1">{{ statusLabel }}</b-badge>
      <span class="text-muted">{{ emptyStateMessage }}</span>
    </div>

    <div v-if="passedAwaitingResult" class="alert alert-info p-1 mb-2">
      <small>
        This student had already passed, so their existing record was linked. The score and
        certificate are being delivered and should appear here shortly.
      </small>
    </div>

    <!-- ── Invite ─────────────────────────────────────────────────────────
         Expiry is shown relative and coloured by urgency: an invite lapsing
         tomorrow should not look identical to one with a month left. -->
    <template v-if="deraData && deraData.inviteExpiresAt && !passedAwaitingResult">
      <h6 class="text-muted mb-25">Invite</h6>
      <p class="mb-1" :class="expiryClass">{{ expiryLabel }}</p>
    </template>

    <!-- The ref matters: vue-clipboard2 needs a container INSIDE the modal,
         see copyInviteUrl. Clicking the field also selects it, so a manual
         copy is possible if the clipboard API is unavailable entirely. -->
    <div v-if="freshInviteUrl" ref="inviteLinkContainer" class="mb-2">
      <b-input-group>
        <b-form-input :value="freshInviteUrl" readonly @focus="$event.target.select()" />
        <b-input-group-append>
          <b-button variant="outline-secondary" @click="copyInviteUrl">Copy</b-button>
        </b-input-group-append>
      </b-input-group>
    </div>

    <!-- ── Documents ──────────────────────────────────────────────────────
         Ingested from DERA. Linked here so staff do not have to go hunting
         through the documents tab for the certificate they just asked about. -->
    <template v-if="documents.length">
      <h6 class="text-muted mb-50">Documents</h6>
      <div
        v-for="doc in documents"
        :key="doc.id"
        class="d-flex align-items-center justify-content-between border-bottom py-50"
      >
        <span class="d-flex align-items-center">
          <feather-icon icon="FileTextIcon" size="15" class="mr-75 text-muted" />
          <span>{{ doc.name }}</span>
        </span>
        <b-button size="sm" variant="flat-secondary" :disabled="busy" @click="openDocument(doc)">Open</b-button>
      </div>
    </template>

    <div v-else-if="hasResult && !loadingDocuments" class="text-muted mb-1">
      <small>No documents have been ingested for this assessment yet.</small>
    </div>

    <!-- ── Actions ────────────────────────────────────────────────────────
         One contextual primary action, so the obvious next step is obvious.
         Replacing a link invalidates the one already emailed, so it is
         separated out and labelled for what it does. -->
    <template #modal-footer>
      <div class="d-flex align-items-center justify-content-between w-100">
        <small v-if="canReplaceLink" class="text-muted mr-1">
          Replacing a link invalidates the one already sent.
        </small>
        <span v-else />

        <span>
          <b-button
            v-if="canReplaceLink"
            variant="outline-secondary"
            size="sm"
            class="mr-1"
            :disabled="busy"
            @click="sendInvite"
          >
            Replace link
          </b-button>

          <b-button
            v-if="hasInvite"
            variant="outline-secondary"
            size="sm"
            class="mr-1"
            :disabled="busy"
            @click="extendExpiry"
          >
            Extend 14 days
          </b-button>

          <b-button :variant="primaryActionVariant" size="sm" :disabled="busy" @click="primaryAction">
            {{ primaryActionLabel }}
          </b-button>
        </span>
      </div>
    </template>
  </b-modal>
</template>

<script>
// BootstrapVue is NOT registered globally in this app — main.js registers only
// ModalPlugin. Every component registers what it uses locally, exactly as
// StudentIdentityModal.vue does. Without this block everything except b-modal
// renders as an unknown element: unstyled, with `variant` and `disabled` inert.
import {
  BBadge,
  BButton,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BModal,
  BProgress,
  BProgressBar,
} from 'bootstrap-vue';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { momentTz } from '@/libs/utils/time';

// The two subCategories mgmt ingests from DERA. `dera` is the certificate and
// predates this feature (staff also upload to it manually); `dera_report` is
// one report per attempt.
const DERA_SUBCATEGORIES = ['dera', 'dera_report'];

export default {
  components: {
    BBadge,
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BModal,
    BProgress,
    BProgressBar,
  },
  props: {
    studentDeraOpen: { type: Boolean, required: true },
    deraData: { type: Object, default: () => ({}) },
    studentId: { type: String, required: true },
  },
  data() {
    // freshInviteUrl holds the URL returned by an invite/resend performed in
    // THIS modal session. It is deliberately not read from the profile:
    // dera.inviteUrl is `private: true` on the model and stripped by toJSON,
    // because the URL is a bearer credential for sitting the assessment.
    return { busy: false, freshInviteUrl: null, documents: [], loadingDocuments: false };
  },
  computed: {
    status() {
      return (this.deraData && this.deraData.status) || 'not_sent';
    },
    hasResult() {
      return Boolean(this.deraData && this.deraData.latest && this.deraData.latest.assessmentCode);
    },
    hasInvite() {
      return Boolean(this.deraData && this.deraData.deraStudentId);
    },
    passedAwaitingResult() {
      return Boolean(this.deraData && this.status === 'passed' && !this.hasResult);
    },
    // A passed student needs no new link, and a student mid-assessment would
    // lose their in-flight attempt if the token rotated.
    canReplaceLink() {
      return this.hasInvite && this.status !== 'passed' && this.status !== 'started';
    },
    statusVariant() {
      switch (this.status) {
        case 'passed':
          return 'success';
        case 'invited':
          return 'info';
        case 'started':
          return 'warning';
        case 'failed':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    statusLabel() {
      return this.status === 'not_sent' ? 'Not sent' : this.status.charAt(0).toUpperCase() + this.status.slice(1);
    },
    emptyStateMessage() {
      switch (this.status) {
        case 'invited':
          return 'The link has been sent. Nothing to review until they submit.';
        case 'started':
          return 'The student has opened the assessment but not submitted it.';
        case 'passed':
          return 'Passed — the result is on its way.';
        default:
          return 'This student has not been sent a DERA link.';
      }
    },
    attemptSummary() {
      const n = (this.deraData.latest && this.deraData.latest.attemptNumber) || 1;
      const total = this.deraData.attempts || n;
      return total > 1 ? `Attempt ${n} of ${total}` : 'First attempt';
    },
    expiryLabel() {
      const at = momentTz(this.deraData.inviteExpiresAt);
      return at.isBefore(momentTz()) ? `Link expired ${at.fromNow()}` : `Link expires ${at.fromNow()}`;
    },
    // Urgency, not just a date: an invite lapsing tomorrow reads differently
    // from one with a month left.
    expiryClass() {
      const at = momentTz(this.deraData.inviteExpiresAt);
      if (at.isBefore(momentTz())) return 'text-danger';
      if (at.diff(momentTz(), 'days') <= 7) return 'text-warning';
      return 'text-muted';
    },
    primaryActionLabel() {
      if (!this.hasInvite) return 'Send DERA link';
      if (this.status === 'failed') return 'Send retake link';
      return 'Resend email';
    },
    primaryActionVariant() {
      return this.status === 'passed' ? 'outline-secondary' : 'primary';
    },
  },
  watch: {
    studentDeraOpen: {
      immediate: true,
      handler(open) {
        if (open) this.loadDocuments();
      },
    },
  },
  methods: {
    onVisibilityChange(value) {
      this.$emit('update:student-dera-open', value);
      // The invite URL is a credential; do not let it outlive the modal.
      if (!value) this.freshInviteUrl = null;
    },
    formatDate(value) {
      return value ? momentTz(value).format('L LT') : '—';
    },
    notify(title, variant, icon) {
      this.$toast({ component: ToastificationContent, props: { title, icon, variant } });
    },
    async loadDocuments() {
      this.loadingDocuments = true;
      try {
        const response = await store.dispatch('studentStoreModule/fetchStudentDocuments', {
          id: this.studentId,
          queryParams: { limit: 50 },
        });
        const all = (response && response.data && (response.data.results || response.data)) || [];
        this.documents = Array.isArray(all) ? all.filter((d) => DERA_SUBCATEGORIES.includes(d.subCategory)) : [];
      } catch (error) {
        // Non-fatal: the rest of the modal is still useful without the list.
        this.documents = [];
      } finally {
        this.loadingDocuments = false;
      }
    },
    async openDocument(doc) {
      this.busy = true;
      try {
        const response = await store.dispatch('studentStoreModule/documentStudentDocumentPreview', {
          studentId: this.studentId,
          documentId: doc.id,
        });
        const url = response && response.data && response.data.url;
        if (url) window.open(url, '_blank', 'noopener');
        else this.notify('Could not open that document', 'danger', 'AlertTriangleIcon');
      } catch (error) {
        this.notify('Could not open that document', 'danger', 'AlertTriangleIcon');
      } finally {
        this.busy = false;
      }
    },
    // Copying from inside a Bootstrap modal is the awkward case, and it is why
    // this is not a one-liner. vue-clipboard2 appends a temporary textarea to
    // `document.body`; the modal traps focus, so execCommand('copy') fails on
    // an element outside it and the copy silently does nothing. Every other
    // $copyText call in this app happens outside a modal, which is why none of
    // them hit this. Passing the container puts that textarea inside the modal.
    async copyInviteUrl() {
      const text = this.freshInviteUrl;
      if (!text) return;

      const viaClipboardApi = async () => {
        if (!navigator.clipboard || !window.isSecureContext) throw new Error('clipboard api unavailable');
        await navigator.clipboard.writeText(text);
      };
      const viaExecCommand = () => this.$copyText(text, this.$refs.inviteLinkContainer);

      try {
        await viaClipboardApi();
      } catch (apiError) {
        try {
          await viaExecCommand();
        } catch (fallbackError) {
          this.notify('Could not copy — select the link and copy it manually', 'danger', 'AlertTriangleIcon');
          return;
        }
      }

      this.notify('Invite link copied', 'success', 'CheckIcon');
    },
    // Every action funnels through here so the busy flag, the toast on failure
    // and the parent refresh are handled once rather than three times.
    async run(action, params, successTitle) {
      this.busy = true;
      try {
        const response = await store.dispatch(`studentStoreModule/${action}`, params);
        const title = typeof successTitle === 'function' ? successTitle(response && response.data) : successTitle;
        this.notify(title, 'success', 'CheckIcon');
        this.$emit('refresh');
        await this.loadDocuments();
        return response;
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || 'Please try again';
        this.notify(message, 'danger', 'AlertTriangleIcon');
        return null;
      } finally {
        this.busy = false;
      }
    },
    primaryAction() {
      return this.hasInvite ? this.resendInvite() : this.sendInvite();
    },
    sendInvite() {
      return this.run(
        'sendDeraInvite',
        { studentId: this.studentId },
        (data) =>
          data && data.alreadyPassed
            ? 'This student had already passed — the existing record was linked'
            : 'DERA link sent'
      ).then((response) => {
        // Capture the URL while we have it — it is never readable from the profile.
        if (response && response.data && response.data.inviteUrl) {
          this.freshInviteUrl = response.data.inviteUrl;
        }
        return response;
      });
    },
    async resendInvite() {
      const response = await this.run('resendDeraInvite', { studentId: this.studentId }, 'DERA email resent');

      if (response && response.data && response.data.inviteUrl) {
        this.freshInviteUrl = response.data.inviteUrl;
      }

      return response;
    },
    extendExpiry() {
      return this.run('extendDeraExpiry', { studentId: this.studentId, extendDays: 14 }, 'Expiry extended 14 days');
    },
  },
};
</script>
